/**
 * Tiny i18n: Norwegian (default) + English toggle.
 *
 * Usage:
 *   const { t, lang, setLang } = useT();
 *   <h1>{t('landing.hero_eyebrow')}</h1>
 *
 * Missing keys fall back to Norwegian, then to the key itself.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DICT } from "./dict";
import { log } from "../lib/log";

const STORAGE_KEY = "srs-lang";
const SUPPORTED = ["nb", "en"];
const DEFAULT_LANG = "nb";

const LangCtx = createContext({ lang: DEFAULT_LANG, setLang: () => {}, t: (k) => k });

function readInitial() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && SUPPORTED.includes(v)) return v;
  } catch (e) {
    log.warn("[i18n] localStorage unavailable:", e?.message);
  }
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readInitial);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); }
    catch (e) { log.warn("[i18n] localStorage unavailable:", e?.message); }
    try { document.documentElement.setAttribute("lang", lang); }
    catch (e) {
      // SSR / non-DOM env: setAttribute will fail. We log at debug level only
      // so prod consoles aren't spammed, but the failure is still observable
      // in browsers with verbose logging enabled.
      log.debug?.("[i18n] document not available:", e?.message);
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  const t = useCallback((key, vars) => {
    const entry = DICT[key];
    let value = (entry && (entry[lang] ?? entry.nb)) ?? key;
    if (vars && typeof value === "string") {
      Object.entries(vars).forEach(([k, v]) => {
        value = value.replace(new RegExp(`\\{${k}\\}`, "g"), v);
      });
    }
    return value;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useT() {
  return useContext(LangCtx);
}

/** Inline pill-button to switch between NB/EN. */
export function LanguageToggle({ style }) {
  const { lang, setLang } = useT();
  const next = lang === "nb" ? "en" : "nb";
  const label = lang === "nb" ? "EN" : "NB";
  return (
    <button
      type="button"
      onClick={() => setLang(next)}
      data-testid="lang-toggle"
      aria-label={lang === "nb" ? "Switch to English" : "Bytt til norsk"}
      title={lang === "nb" ? "Switch to English" : "Bytt til norsk"}
      style={{
        background: "transparent",
        border: "1px solid var(--srs-line)",
        borderRadius: 999,
        padding: "6px 12px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.16em",
        color: "var(--srs-ink-dim)",
        cursor: "pointer",
        fontFamily: "'IBM Plex Mono', monospace",
        transition: "all 0.18s ease",
        ...style,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = "var(--srs-ink)"; e.currentTarget.style.borderColor = "var(--srs-ink)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = "var(--srs-ink-dim)"; e.currentTarget.style.borderColor = "var(--srs-line)"; }}
    >
      {label}
    </button>
  );
}
